import { Property } from '../types';

/**
 * Checks if an owner string represents special negotiation.
 * Matches variants such as NEGOCIACION_ESPECIAL, NEGOCIACION-ESPECIAL,
 * with underscores, hyphens, or spaces.
 */
export const isSpecialNegotiationOwner = (val?: string | null): boolean => {
  if (!val) return false;
  const normalized = String(val).toUpperCase().trim().replace(/[-_ ]/g, '');
  return normalized === 'NEGOCIACIONESPECIAL' || normalized.includes('NEGOCIACIONESPECIAL');
};

/**
 * Normalizes owner string for general comparison.
 */
export const normalizeOwnerForFilter = (val?: string | null): string => {
  return String(val || '')
    .trim()
    .toLowerCase()
    .replace(/[- ]/g, '_');
};

/**
 * Determines if a property's owner matches the given owner filter.
 * Treats NEGOCIACION_ESPECIAL and NEGOCIACION-ESPECIAL as equivalent.
 */
export const matchesOwnerFilter = (
  rawOwner: string | undefined | null,
  filterOwner: string | undefined | null
): boolean => {
  if (!filterOwner) return true;
  const trimmedFilter = String(filterOwner).trim();
  if (!trimmedFilter) return true;

  const trimmedRaw = String(rawOwner || '').trim();

  if (isSpecialNegotiationOwner(trimmedFilter)) {
    return isSpecialNegotiationOwner(trimmedRaw);
  }

  const normFilter = normalizeOwnerForFilter(trimmedFilter);
  const normRaw = normalizeOwnerForFilter(trimmedRaw);
  return normRaw === normFilter;
};

/**
 * Ensures allowed owners include both NEGOCIACION_ESPECIAL and NEGOCIACION-ESPECIAL.
 */
export const ensureSpecialNegotiationInOwners = (owners: string[]): string[] => {
  const set = new Set(owners);
  if (set.has('NEGOCIACION_ESPECIAL') || set.has('NEGOCIACION-ESPECIAL') || set.size > 0) {
    set.add('NEGOCIACION_ESPECIAL');
    set.add('NEGOCIACION-ESPECIAL');
  }
  return Array.from(set).sort();
};

/**
 * Evaluates whether a property matches all 183150 operation query filters.
 */
export const filter183150Property = (
  p: Property,
  query: {
    status?: string;
    owner?: string;
    search?: string;
    brpCode?: string;
    propertyCode?: string;
  }
): boolean => {
  const code = String(p.codigo || '');
  const id = String(p.id || '');
  const rawOwner = String(p.owner || (p as any).propietario || '');
  const brp = String(p.codigo_brp || (p as any).codigo_brp_o_inmobiliaria || '');

  const is183150 =
    code.startsWith('183150') ||
    id.startsWith('183150') ||
    rawOwner.includes('183150') ||
    brp.startsWith('183150');

  if (!is183150) return false;

  if (query.status && (p.status || 'Activo').toLowerCase() !== query.status.toLowerCase()) {
    return false;
  }
  if (query.owner && !matchesOwnerFilter(rawOwner, query.owner)) {
    return false;
  }
  if (query.brpCode && !brp.toLowerCase().includes(query.brpCode.toLowerCase())) {
    return false;
  }
  if (query.propertyCode && !code.toLowerCase().includes(query.propertyCode.toLowerCase())) {
    return false;
  }
  if (query.search) {
    const haystack = `${code} ${p.tipoinmueble || ''} ${p.ciudad || ''} ${p.direccion || ''} ${brp}`.toLowerCase();
    if (!haystack.includes(query.search.toLowerCase())) return false;
  }

  return true;
};
